"use client";

import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";

type Rgb = { r: number; g: number; b: number };
type Hsl = { h: number; s: number; l: number };
type Hsb = { h: number; s: number; b: number };

const DEFAULT_COLOR = "#4F46E5";

/* --------------------------------- Helpers -------------------------------- */

/** Returns "#RRGGBB" (uppercase) for valid 3 or 6-digit hex, otherwise null. */
function parseHex(value: string): string | null {
  const cleaned = value.trim().replace(/^#/, "");
  if (/^[0-9a-fA-F]{6}$/.test(cleaned)) return `#${cleaned.toUpperCase()}`;
  if (/^[0-9a-fA-F]{3}$/.test(cleaned)) {
    return `#${cleaned
      .split("")
      .map((char) => char + char)
      .join("")
      .toUpperCase()}`;
  }
  return null;
}

function hexToRgb(hex: string): Rgb {
  const number = Number.parseInt(hex.replace("#", ""), 16);
  return { r: (number >> 16) & 255, g: (number >> 8) & 255, b: number & 255 };
}

function rgbToHex({ r, g, b }: Rgb): string {
  return `#${[r, g, b]
    .map((channel) => Math.max(0, Math.min(255, Math.round(channel))).toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase()}`;
}

function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const l = (max + min) / 2;
  const s = delta === 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));

  let h = 0;
  if (delta !== 0) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
  }

  return {
    h: Math.round((h * 60 + 360) % 360) % 360,
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

function hslToRgb({ h, s, l }: Hsl): Rgb {
  const saturation = s / 100;
  const lightness = l / 100;
  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = lightness - chroma / 2;

  let red = 0;
  let green = 0;
  let blue = 0;
  if (h < 60) [red, green, blue] = [chroma, x, 0];
  else if (h < 120) [red, green, blue] = [x, chroma, 0];
  else if (h < 180) [red, green, blue] = [0, chroma, x];
  else if (h < 240) [red, green, blue] = [0, x, chroma];
  else if (h < 300) [red, green, blue] = [x, 0, chroma];
  else [red, green, blue] = [chroma, 0, x];

  return { r: (red + m) * 255, g: (green + m) * 255, b: (blue + m) * 255 };
}

function rgbToHsb({ r, g, b }: Rgb): Hsb {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const delta = max - min;
  const s = max === 0 ? 0 : delta / max;

  let h = 0;
  if (delta !== 0) {
    if (max === red) h = ((green - blue) / delta) % 6;
    else if (max === green) h = (blue - red) / delta + 2;
    else h = (red - green) / delta + 4;
  }

  return {
    h: Math.round((h * 60 + 360) % 360) % 360,
    s: Math.round(s * 100),
    b: Math.round(max * 100),
  };
}

/** WCAG relative luminance. */
function luminance({ r, g, b }: Rgb): number {
  const [red, green, blue] = [r, g, b].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
}

function contrastRatio(first: Rgb, second: Rgb): number {
  const a = luminance(first);
  const b = luminance(second);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** Real shades and tints: same hue and saturation, varying lightness. */
function buildPalette(hex: string): string[] {
  const { h, s } = rgbToHsl(hexToRgb(hex));
  return [92, 80, 65, 50, 35, 20, 10].map((lightness) =>
    rgbToHex(hslToRgb({ h, s, l: lightness })),
  );
}

/* -------------------------------- Component ------------------------------- */

type EyeDropperCtor = new () => { open: () => Promise<{ sRGBHex: string }> };

export default function ColorPicker({}: { toolName: string }) {
  const copyTimer = useRef<number | undefined>(undefined);

  const [color, setColor] = useState(DEFAULT_COLOR); // always valid "#RRGGBB"
  const [hexDraft, setHexDraft] = useState(DEFAULT_COLOR); // what the user is typing
  const [copiedKey, setCopiedKey] = useState<string | null>(null);
  const [copyFailed, setCopyFailed] = useState(false);
  const [message, setMessage] = useState("");

  const supportsEyeDropper = useSyncExternalStore(
    () => () => undefined,
    () => typeof window !== "undefined" && "EyeDropper" in window,
    () => false,
  );

  useEffect(() => {
    return () => window.clearTimeout(copyTimer.current);
  }, []);

  const hexIsValid = parseHex(hexDraft) !== null;

  const applyColor = useCallback((next: string) => {
    setColor(next);
    setHexDraft(next);
    setMessage("");
  }, []);

  const rgb = useMemo(() => hexToRgb(color), [color]);
  const hsl = useMemo(() => rgbToHsl(rgb), [rgb]);
  const hsb = useMemo(() => rgbToHsb(rgb), [rgb]);
  const palette = useMemo(() => buildPalette(color), [color]);

  const whiteContrast = contrastRatio(rgb, { r: 255, g: 255, b: 255 });
  const blackContrast = contrastRatio(rgb, { r: 0, g: 0, b: 0 });
  const bestText = whiteContrast >= blackContrast ? "#FFFFFF" : "#000000";
  const bestRatio = Math.max(whiteContrast, blackContrast);

  async function copy(value: string, key: string) {
    window.clearTimeout(copyTimer.current);
    try {
      await navigator.clipboard.writeText(value);
      setCopiedKey(key);
      setCopyFailed(false);
    } catch {
      setCopiedKey(null);
      setCopyFailed(true);
    }
    copyTimer.current = window.setTimeout(() => {
      setCopiedKey(null);
      setCopyFailed(false);
    }, 1400);
  }

  async function pickFromScreen() {
    const EyeDropper = (window as unknown as { EyeDropper?: EyeDropperCtor }).EyeDropper;
    if (!EyeDropper) return;
    try {
      const result = await new EyeDropper().open();
      const parsed = parseHex(result.sRGBHex);
      if (parsed) applyColor(parsed);
    } catch {
      // The user pressed Escape. Nothing to do.
    }
  }

  function handleHexChange(value: string) {
    setHexDraft(value);
    const parsed = parseHex(value);
    if (parsed) {
      setColor(parsed);
      setMessage("");
    }
  }

  function handleHexBlur() {
    const parsed = parseHex(hexDraft);
    if (parsed) {
      setHexDraft(parsed);
    } else {
      // Restore the last good color instead of leaving invalid text behind.
      setHexDraft(color);
      setMessage("That wasn't a valid HEX code, so the last valid color was restored.");
    }
  }

  const formats = [
    { label: "HEX", value: color },
    { label: "RGB", value: `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})` },
    { label: "HSL", value: `hsl(${hsl.h}, ${hsl.s}%, ${hsl.l}%)` },
    { label: "HSB", value: `${hsb.h}°, ${hsb.s}%, ${hsb.b}%`, note: "Reference only. Not valid CSS." },
  ];

  return (
    <div className="space-y-4">
      <div className="grid gap-4 md:grid-cols-2">
        <label className="text-sm font-medium text-slate-700">
          Pick a color
          <input
            type="color"
            value={color.toLowerCase()}
            onChange={(event) => applyColor(event.target.value.toUpperCase())}
            className="mt-2 h-12 w-full cursor-pointer rounded-lg border border-slate-300"
          />
        </label>

        <div className="text-sm font-medium text-slate-700">
          <label htmlFor="color-hex-input">HEX value</label>
          <div className="mt-2 flex gap-2">
            <input
              id="color-hex-input"
              type="text"
              value={hexDraft}
              onChange={(event) => handleHexChange(event.target.value)}
              onBlur={handleHexBlur}
              spellCheck={false}
              autoComplete="off"
              maxLength={7}
              aria-invalid={!hexIsValid}
              aria-describedby="color-hex-help"
              placeholder="#4F46E5"
              className={`w-full rounded-lg border px-3 py-2 font-mono text-sm outline-none focus:ring-2 ${
                hexIsValid
                  ? "border-slate-300 focus:border-indigo-500 focus:ring-indigo-200"
                  : "border-rose-400 focus:ring-rose-200"
              }`}
            />
            {supportsEyeDropper && (
              <button
                type="button"
                onClick={() => void pickFromScreen()}
                className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800"
              >
                Pick from screen
              </button>
            )}
          </div>
          <p id="color-hex-help" className={`mt-1 text-xs ${hexIsValid ? "text-slate-500" : "text-rose-600"}`}>
            {hexIsValid ? "Type 3 or 6 hex digits, with or without #." : "Enter a valid HEX code, like #4F46E5 or 4F4."}
          </p>
        </div>
      </div>

      {message && (
        <p role="status" className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          {message}
        </p>
      )}

      <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div
          className="flex h-28 w-full items-center justify-center rounded-xl border border-slate-200"
          style={{ backgroundColor: color, color: bestText }}
        >
          <span className="text-lg font-bold">Aa</span>
          <span className="ml-3 text-sm font-medium">{color}</span>
        </div>
        <p className="mt-3 text-sm font-semibold text-slate-900">Live preview</p>
        <p className="text-xs text-slate-500">
          Best text color: {bestText === "#FFFFFF" ? "white" : "black"} · contrast {bestRatio.toFixed(1)}:1{" "}
          {bestRatio >= 7 ? "(AAA)" : bestRatio >= 4.5 ? "(AA)" : bestRatio >= 3 ? "(large text only)" : "(low contrast)"}
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        {formats.map((entry) => (
          <div key={entry.label} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{entry.label}</p>
                <p className="mt-1 truncate font-mono text-sm font-semibold text-slate-900">{entry.value}</p>
                {entry.note && <p className="text-xs text-slate-500">{entry.note}</p>}
              </div>
              <button
                type="button"
                onClick={() => void copy(entry.value, entry.label)}
                aria-label={`Copy ${entry.label} value`}
                className="shrink-0 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-800"
              >
                {copiedKey === entry.label ? "Copied!" : copyFailed ? "Failed" : "Copy"}
              </button>
            </div>
          </div>
        ))}
      </div>

      <div>
        <p className="text-sm font-semibold text-slate-900">Shades and tints</p>
        <p className="text-xs text-slate-500">Same hue, from light to dark. Click any swatch to copy its HEX.</p>
        <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {palette.map((swatch) => (
            <button
              key={swatch}
              type="button"
              onClick={() => void copy(swatch, swatch)}
              aria-label={`Copy ${swatch}`}
              className="group rounded-xl border border-slate-200 bg-slate-50 p-2 text-left transition hover:border-indigo-400"
            >
              <div className="h-14 rounded-lg border border-slate-200" style={{ backgroundColor: swatch }} />
              <p className="mt-2 font-mono text-xs font-medium text-slate-700">
                {copiedKey === swatch ? "Copied!" : swatch}
              </p>
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => applyColor(DEFAULT_COLOR)}
          className="mt-3 rounded-lg bg-slate-100 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-200"
        >
          Reset to default color
        </button>
      </div>
    </div>
  );
}